import React, { useEffect, useState, lazy } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Wave } from '../components/Wave';
import { AnimatedSection } from '../components/AnimatedSection';
import { useLocation } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import { getPageSEO } from '../data/seoData';

export const Contact = () => {
  // SEO Configuration
  useSEO(getPageSEO('contact'));

  const location = useLocation();
  const [showTechnologies, setShowTechnologies] = useState(false);
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const showTechParam = queryParams.get('showTech') === 'true';
    const showTechState = location.state?.showTechnologies === true;
    setShowTechnologies(showTechParam || showTechState);
  }, [location]);
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<{
    [key: string]: string;
  }>({});
  const validateForm = () => {
    const newErrors: {
      [key: string]: string;
    } = {};
    if (!formState.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }
    if (!formState.email.trim()) {
      newErrors.email = 'El correo es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      newErrors.email = 'Formato de correo inválido';
    }
    if (!formState.message.trim()) {
      newErrors.message = 'El mensaje es requerido';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setTimeout(() => {
        setFormStatus('success');
        setFormState({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        setTimeout(() => {
          setFormStatus('idle');
        }, 3000);
      }, 1000);
    }
  };
  return <div className="w-full">
      <section className="bg-gradient-to-b from-blue-800 via-blue-700 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">Contáctanos</h1>
          <p className="text-xl mb-8">
            Estamos aquí para escucharte y brindarte la asistencia que necesitas
          </p>
        </div>
      </section>
      <Wave color="#fff" className="bg-blue-600" pattern="pattern1" />
      {showTechnologies && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }}>
          <AnimatedSection className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold mb-4">Tecnologías</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Trabajamos con las tecnologías más modernas y eficientes para
                  crear soluciones robustas y escalables.
                </p>
              </div>
              <div className="mb-16">
                <h3 className="text-2xl font-semibold mb-8 text-blue-600">
                  Frontend
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
                  {[{
                name: 'React',
                logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a7/React-icon.svg/1150px-React-icon.svg.png'
              }, {
                name: 'TypeScript',
                logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4c/Typescript_logo_2020.svg/2048px-Typescript_logo_2020.svg.png'
              }, {
                name: 'JavaScript',
                logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6a/JavaScript-logo.png/800px-JavaScript-logo.png'
              }, {
                name: 'Tailwind CSS',
                logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d5/Tailwind_CSS_Logo.svg/2048px-Tailwind_CSS_Logo.svg.png'
              }, {
                name: 'HTML5',
                logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/61/HTML5_logo_and_wordmark.svg/1452px-HTML5_logo_and_wordmark.svg.png'
              }, {
                name: 'CSS3',
                logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d5/CSS3_logo_and_wordmark.svg/1452px-CSS3_logo_and_wordmark.svg.png'
              }].map((tech, index) => <motion.div key={tech.name} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                      <img src={tech.logo} alt={tech.name} className="h-12 w-auto object-contain" />
                      <span className="mt-2 text-sm text-gray-600">
                        {tech.name}
                      </span>
                    </motion.div>)}
                </div>
              </div>
              <div className="mb-16">
                <h3 className="text-2xl font-semibold mb-8 text-green-600">
                  Backend
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
                  {[{
                name: 'Node.js',
                logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d9/Node.js_logo.svg/2560px-Node.js_logo.svg.png'
              }, {
                name: 'Express',
                logo: 'https://upload.wikimedia.org/wikipedia/commons/6/64/Expressjs.png'
              }, {
                name: 'C#',
                logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0d/C_Sharp_wordmark.svg/1200px-C_Sharp_wordmark.svg.png'
              }, {
                name: 'Java',
                logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/3/30/Java_programming_language_logo.svg/1200px-Java_programming_language_logo.svg.png'
              }].map((tech, index) => <motion.div key={tech.name} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                      <img src={tech.logo} alt={tech.name} className="h-12 w-auto object-contain" />
                      <span className="mt-2 text-sm text-gray-600">
                        {tech.name}
                      </span>
                    </motion.div>)}
                </div>
              </div>
              <div className="mb-16">
                <h3 className="text-2xl font-semibold mb-8 text-orange-600">
                  Bases de Datos
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
                  {[{
                name: 'MySQL',
                logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/d/dd/MySQL_logo.svg/1280px-MySQL_logo.svg.png'
              }, {
                name: 'MariaDB',
                logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/ca/MariaDB_colour_logo.svg/2560px-MariaDB_colour_logo.svg.png'
              }].map((tech, index) => <motion.div key={tech.name} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                      <img src={tech.logo} alt={tech.name} className="h-12 w-auto object-contain" />
                      <span className="mt-2 text-sm text-gray-600">
                        {tech.name}
                      </span>
                    </motion.div>)}
                </div>
              </div>
              <div className="mb-16">
                <h3 className="text-2xl font-semibold mb-8 text-red-600">
                  Inteligencia Artificial
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
                  {[{
                name: 'OpenAI',
                logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4d/OpenAI_Logo.svg/2560px-OpenAI_Logo.svg.png'
              }, {
                name: 'Gemini AI',
                logo: 'https://seeklogo.com/images/G/gemini-ai-logo-A5C53986DE-seeklogo.com.png'
              }, {
                name: 'DeepSeek',
                logo: 'https://assets-global.website-files.com/65772d00aaa1d131f0cd34b7/65772d00aaa1d131f0cd34d1_Logo.svg'
              }].map((tech, index) => <motion.div key={tech.name} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                      <img src={tech.logo} alt={tech.name} className="h-12 w-auto object-contain" />
                      <span className="mt-2 text-sm text-gray-600">
                        {tech.name}
                      </span>
                    </motion.div>)}
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-8 text-yellow-600">
                  Móvil
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
                  {[{
                name: 'iOS',
                logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/ca/IOS_logo.svg/2048px-IOS_logo.svg.png'
              }, {
                name: 'Android',
                logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d7/Android_robot.svg/872px-Android_robot.svg.png'
              }, {
                name: 'Flutter',
                logo: 'https://storage.googleapis.com/cms-storage-bucket/0dbfcc7a59cd1cf16282.png'
              }].map((tech, index) => <motion.div key={tech.name} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                      <img src={tech.logo} alt={tech.name} className="h-12 w-auto object-contain" />
                      <span className="mt-2 text-sm text-gray-600">
                        {tech.name}
                      </span>
                    </motion.div>)}
                </div>
              </div>
            </div>
          </AnimatedSection>
        </motion.div>}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-12">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="order-1 lg:order-2">
              <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
                <h2 className="text-3xl font-bold mb-8 text-gray-900 text-center">
                  Información de Contacto
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <motion.div className="text-center p-6 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 transition-all duration-300" whileHover={{
                  y: -5
                }} transition={{
                  type: 'spring',
                  stiffness: 300
                }}>
                    <div className="bg-blue-600 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <Mail className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="font-bold text-lg text-gray-900 mb-3">Correo Electrónico</h3>
                    <div className="space-y-2">
                      <div>
                        <p className="text-gray-600 text-sm font-medium mb-1">Ventas</p>
                        <a href="mailto:gerenciacomercial@ipse.com.ec" className="text-blue-700 hover:text-blue-800 font-medium text-sm break-all hover:underline">
                          gerenciacomercial@ipse.com.ec
                        </a>
                      </div>
                      <div className="pt-2">
                        <p className="text-gray-600 text-sm font-medium mb-1">Soporte</p>
                        <a href="mailto:soporte@ipse.com.ec" className="text-blue-700 hover:text-blue-800 font-medium text-sm hover:underline">
                          soporte@ipse.com.ec
                        </a>
                      </div>
                    </div>
                  </motion.div>
                  
                  <motion.div className="text-center p-6 rounded-xl bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 transition-all duration-300" whileHover={{
                  y: -5
                }} transition={{
                  type: 'spring',
                  stiffness: 300
                }}>
                    <div className="bg-green-600 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <Phone className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="font-bold text-lg text-gray-900 mb-3">Teléfono</h3>
                    <div>
                      <p className="text-gray-600 text-sm font-medium mb-2">Ventas y Soporte</p>
                      <div className="flex flex-col items-center space-y-2">
                        <a href="tel:+593979652188" className="text-green-700 hover:text-green-800 font-bold text-lg hover:underline">
                          +593 97 965 2188
                        </a>
                        <a href="https://wa.me/593979652188" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors font-medium text-sm">
                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                          </svg>
                          WhatsApp
                        </a>
                      </div>
                    </div>
                  </motion.div>
                  
                  <motion.div className="text-center p-6 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 transition-all duration-300" whileHover={{
                  y: -5
                }} transition={{
                  type: 'spring',
                  stiffness: 300
                }}>
                    <div className="bg-purple-600 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <MapPin className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="font-bold text-lg text-gray-900 mb-3">Dirección</h3>
                    <div>
                      <p className="text-gray-600 text-sm font-medium mb-2">Oficina Principal</p>
                      <a href="https://maps.app.goo.gl/TjSiYKsU8W3VZBTf9" target="_blank" rel="noopener noreferrer" className="text-purple-700 hover:text-purple-800 hover:underline">
                        <div className="font-semibold text-base leading-relaxed">
                          Guayaquil<br />
                          <span className="text-sm">Edificio The Point</span><br />
                          <span className="text-sm">Piso 18, Oficina 03</span>
                        </div>
                      </a>
                    </div>
                  </motion.div>
                </div>
              </div>
              
              {/* Mapa de Google Maps */}
              <div 
                className="bg-gray-50 rounded-2xl shadow-lg overflow-hidden"
              >
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-xl font-bold text-gray-800">Nuestra Ubicación</h3>
                  <p className="text-gray-600 mt-1">
                    Visítanos en nuestra oficina en Guayaquil
                  </p>
                </div>
                <div className="relative">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3969.3377!2d-79.8973!3d-2.1712!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x902d13cbe855b5b9%3A0xf5a9b1b9b8b4b4b4!2sEdificio%20The%20Point!5e0!3m2!1sen!2sec!4v1699999999999!5m2!1sen!2sec"
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full"
                  />
                  <div className="absolute top-4 right-4">
                    <a
                      href="https://maps.app.goo.gl/TjSiYKsU8W3VZBTf9"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow text-sm font-medium text-gray-700 hover:text-blue-600"
                    >
                      <MapPin className="h-4 w-4 mr-2" />
                      Ver en Google Maps
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
};